import { useCallback, useState } from 'react';
import type { CubeFile } from '@stores/conversionStore';

/** Maximum file size in bytes (50MB per FR-006) */
const MAX_FILE_SIZE = 50 * 1024 * 1024;

/** Maximum files per batch (50 per FR-007) */
const MAX_BATCH_SIZE = 50;

/** Accepted file extensions */
const ACCEPTED_EXTENSIONS = ['.cube'];

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

export interface UseFileUploadReturn {
  /** Validate a single file */
  validateFile: (file: File) => FileValidationResult;
  /** Validate multiple files for batch mode */
  validateBatch: (files: File[]) => { valid: File[]; errors: string[] };
  /** Create CubeFile objects from validated files */
  createCubeFiles: (files: File[]) => CubeFile[];
  /** Current error message */
  error: string | null;
  /** Clear error */
  clearError: () => void;
  /** Set error */
  setError: (error: string) => void;
}

/**
 * Hook for file upload validation.
 * Validates file extension, size, and batch limits.
 */
export function useFileUpload(): UseFileUploadReturn {
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const validateFile = useCallback((file: File): FileValidationResult => {
    // Check extension
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return {
        valid: false,
        error: 'Only .cube files are accepted',
      };
    }

    // Check size (FR-006)
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: 'File exceeds 50MB limit',
      };
    }

    return { valid: true };
  }, []);

  const validateBatch = useCallback(
    (files: File[]): { valid: File[]; errors: string[] } => {
      // Check batch size (FR-007)
      if (files.length > MAX_BATCH_SIZE) {
        return {
          valid: [],
          errors: [`Maximum 50 files per batch. Please reduce selection. (got ${files.length})`],
        };
      }

      const valid: File[] = [];
      const errors: string[] = [];

      for (const file of files) {
        const result = validateFile(file);
        if (result.valid) {
          valid.push(file);
        } else {
          errors.push(`${file.name}: ${result.error}`);
        }
      }

      return { valid, errors };
    },
    [validateFile]
  );

  /** Generate unique ID for file tracking */
  const generateId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }, []);

  /** Create CubeFile objects from File objects */
  const createCubeFiles = useCallback(
    (files: File[]): CubeFile[] => {
      return files.map((file) => ({
        id: generateId(),
        filename: file.name,
        content: '', // Will be read on conversion
        size: file.size,
        status: 'pending' as const,
        file,
      }));
    },
    [generateId]
  );

  return {
    validateFile,
    validateBatch,
    createCubeFiles,
    error,
    clearError,
    setError,
  };
}

export default useFileUpload;
